import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Upload, X, Image as ImgIcon } from "lucide-react";

const TYPES = [
  { v: "rumah", l: "Rumah" }, { v: "apartemen", l: "Apartemen" },
  { v: "vila", l: "Vila" }, { v: "ruko", l: "Ruko" },
  { v: "lahan", l: "Lahan" }, { v: "lainnya", l: "Lainnya" },
];
const FACILITIES = ["Kolam Renang", "Garasi", "Taman", "Furnished", "Keamanan 24 Jam", "Lift", "Playground", "Mushola"];

const EMPTY = {
  title: "", description: "", location: "", address: "",
  lat: null, lng: null, price: "", property_type: "rumah",
  land_area: "", building_area: "", bedrooms: "", bathrooms: "",
  facilities: [], status: "baru", photos: [], is_published: true,
};

export default function ListingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/listings/${id}`);
        const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v == null ? "" : v]));
        setForm({ ...EMPTY, ...clean });
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFac = (f) => setForm((s) => ({ ...s, facilities: s.facilities.includes(f) ? s.facilities.filter((x) => x !== f) : [...s.facilities, f] }));

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles((f) => [...f, ...list]);
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));
  const removeExisting = (p) => set("photos", form.photos.filter((x) => x !== p));

  const aiGenerate = async () => {
    if (!form.title || !form.location) {
      toast.error("Isi judul & lokasi dulu");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate-description", {
        title: form.title, location: form.location, property_type: form.property_type,
        price: Number(form.price || 0),
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        land_area: form.land_area ? Number(form.land_area) : null,
        building_area: form.building_area ? Number(form.building_area) : null,
        facilities: form.facilities,
      });
      set("description", data.description);
      toast.success("Deskripsi dihasilkan AI");
    } catch (e) {
      toast.error(e.response?.status === 401 ? formatApiError(e) : "Fitur AI tidak tersedia, isi deskripsi manual");
    } finally { setAiLoading(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price || 0),
        land_area: form.land_area ? Number(form.land_area) : null,
        building_area: form.building_area ? Number(form.building_area) : null,
        bedrooms: form.bedrooms !== "" && form.bedrooms != null ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms !== "" && form.bathrooms != null ? Number(form.bathrooms) : null,
        lat: form.lat !== "" && form.lat != null ? Number(form.lat) : null,
        lng: form.lng !== "" && form.lng != null ? Number(form.lng) : null,
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/listings/${id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/listings", payload);
        saved = data;
      }
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        try {
          await api.post(`/listings/${saved.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (upErr) {
          toast.error(`Listing tersimpan, tapi upload foto gagal: ${formatApiError(upErr)}. Silakan coba upload lagi.`);
          setFiles([]);
          if (!isEdit) navigate(`/app/listings/${saved.id}/edit`, { replace: true });
          return;
        }
      }
      toast.success(isEdit ? "Listing diperbarui" : "Listing berhasil disimpan");
      navigate(`/app/listings/${saved.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="listing-form-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Kembali
      </button>
      <div>
        <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">{isEdit ? "Edit Listing" : "Listing Baru"}</span>
        <h1 className="text-3xl font-extrabold tracking-tight mt-1">{isEdit ? "Perbarui Properti" : "Tambah Listing"}</h1>
      </div>

      <form onSubmit={onSubmit} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-xl bg-[#141C2E] border border-white/5 p-6 space-y-4">
            <h3 className="font-semibold">Info Dasar</h3>
            <div>
              <Label>Judul Listing *</Label>
              <Input required value={form.title} data-testid="form-title"
                     onChange={(e) => set("title", e.target.value)}
                     className="mt-1 bg-[#0D1322] border-white/10" placeholder="Rumah Modern Minimalis BSD" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Tipe *</Label>
                <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="form-type" className="mt-1 bg-[#0D1322] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
                    {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Harga (Rp) *</Label>
                <Input required type="number" min="0" value={form.price} data-testid="form-price"
                       onChange={(e) => set("price", e.target.value)}
                       className="mt-1 bg-[#0D1322] border-white/10" placeholder="2500000000" />
              </div>
            </div>
            <div>
              <Label>Lokasi (Kota/Area) *</Label>
              <Input required value={form.location} data-testid="form-location"
                     onChange={(e) => set("location", e.target.value)}
                     className="mt-1 bg-[#0D1322] border-white/10" placeholder="BSD City, Tangerang Selatan" />
            </div>
            <div>
              <Label>Alamat Lengkap</Label>
              <Input value={form.address} data-testid="form-address"
                     onChange={(e) => set("address", e.target.value)}
                     className="mt-1 bg-[#0D1322] border-white/10" placeholder="Jl. Sutera Jingga No. 12" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Lat (opsional)</Label>
                <Input type="number" step="any" value={form.lat ?? ""} onChange={(e) => set("lat", e.target.value === "" ? null : e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" placeholder="-6.3017" data-testid="form-lat" />
              </div>
              <div>
                <Label>Lng (opsional)</Label>
                <Input type="number" step="any" value={form.lng ?? ""} onChange={(e) => set("lng", e.target.value === "" ? null : e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" placeholder="106.6520" data-testid="form-lng" />
              </div>
            </div>
          </section>

          <section className="rounded-xl bg-[#141C2E] border border-white/5 p-6 space-y-4">
            <h3 className="font-semibold">Spesifikasi</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>LT (m²)</Label><Input type="number" min="0" value={form.land_area} data-testid="form-lt" onChange={(e) => set("land_area", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
              <div><Label>LB (m²)</Label><Input type="number" min="0" value={form.building_area} data-testid="form-lb" onChange={(e) => set("building_area", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
              <div><Label>K. Tidur</Label><Input type="number" min="0" value={form.bedrooms} data-testid="form-kt" onChange={(e) => set("bedrooms", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
              <div><Label>K. Mandi</Label><Input type="number" min="0" value={form.bathrooms} data-testid="form-km" onChange={(e) => set("bathrooms", e.target.value)} className="mt-1 bg-[#0D1322] border-white/10" /></div>
            </div>
            <div>
              <Label>Fasilitas</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {FACILITIES.map((f) => {
                  const active = form.facilities.includes(f);
                  return (
                    <button type="button" key={f}
                            data-testid={`facility-${f.replace(/\s+/g, '-').toLowerCase()}`}
                            onClick={() => toggleFac(f)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              active ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-[#0D1322] border-white/10 text-slate-400 hover:border-white/30"
                            }`}>
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="rounded-xl bg-[#141C2E] border border-white/5 p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Deskripsi</h3>
              <Button type="button" onClick={aiGenerate} disabled={aiLoading}
                      data-testid="ai-generate-btn"
                      className="bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-200 h-8">
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                {aiLoading ? "Membuat…" : "AI Copywriting"}
              </Button>
            </div>
            <Textarea value={form.description} data-testid="form-description"
                      onChange={(e) => set("description", e.target.value)}
                      rows={8}
                      className="bg-[#0D1322] border-white/10 resize-none"
                      placeholder="Deskripsikan properti ini — atau gunakan AI Copywriting." />
          </section>

          <section className="rounded-xl bg-[#141C2E] border border-white/5 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Foto (Watermark Otomatis)</h3>
              <label className="cursor-pointer px-3 py-1.5 rounded-lg border border-white/10 bg-[#0D1322] hover:border-orange-500/40 text-sm flex items-center gap-2" data-testid="photo-upload-trigger">
                <Upload className="w-4 h-4" /> Pilih Foto
                <input type="file" multiple accept="image/*" className="hidden" onChange={onFiles} data-testid="photo-upload-input" />
              </label>
            </div>
            {(form.photos?.length || 0) + files.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/10 p-10 text-center text-slate-500">
                <ImgIcon className="w-6 h-6 mx-auto mb-2" />
                Belum ada foto. Multi-upload didukung, akan diberi watermark otomatis.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {form.photos?.map((p) => (
                  <div key={p} className="relative rounded-lg overflow-hidden group border border-white/10">
                    <img src={fileUrl(p)} alt="" className="w-full h-32 object-cover" />
                    <button type="button" onClick={() => removeExisting(p)} className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`remove-existing-${p}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {files.map((f, i) => (
                  <div key={i} className="relative rounded-lg overflow-hidden group border border-orange-500/20">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-32 object-cover" />
                    <div className="absolute bottom-1 left-1 text-[10px] bg-orange-500/80 text-white px-1.5 py-0.5 rounded">Baru</div>
                    <button type="button" onClick={() => removeFile(i)} className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-xl bg-[#141C2E] border border-white/5 p-6 space-y-4 sticky top-24">
            <h3 className="font-semibold">Publikasi</h3>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="form-status" className="mt-1 bg-[#0D1322] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
                  <SelectItem value="baru">Baru</SelectItem>
                  <SelectItem value="on_market">On Market</SelectItem>
                  <SelectItem value="nego">Negosiasi</SelectItem>
                  <SelectItem value="closing">Closing</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                  <SelectItem value="sold">SOLD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm font-medium">Tampilkan di katalog publik</div>
                <div className="text-xs text-slate-500">Shareable link &amp; QR code</div>
              </div>
              <Switch checked={form.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="form-published" />
            </div>
            <Button type="submit" disabled={saving} data-testid="form-submit"
                    className="w-full bg-gradient-to-r from-[#FF5722] to-[#F97316] hover:from-[#F97316] hover:to-[#EA580C] font-semibold py-6">
              {saving ? "Menyimpan…" : (isEdit ? "Perbarui Listing" : "Simpan Listing")}
            </Button>
          </section>
        </aside>
      </form>
    </div>
  );
}
